import axios from "axios";
import {
  TipoErro,
  CHAVE_I18N,
  tipoPorStatus,
  pareceTecnica,
  ehSentinelaGenerica,
  ehErroSistemico,
  TIPO_DO_BACKEND,
  STATUS_GENERICO,
} from "./catalogo";

// i18n é carregado sob demanda de propósito: o api.service é importado pelo
// store (autenticacao.module), e manter este módulo sem dependência no topo
// evita que um ciclo futuro enxergue exports ainda não inicializados.
let _i18n = null;

// Se o i18n ainda não estiver pronto, o usuário precisa ver alguma coisa útil --
// nunca um aviso vazio ou o nome da chave.
const FALLBACK = {
  REDE: "Não conseguimos falar com o servidor. Verifique sua conexão e tente novamente.",
  SISTEMICO:
    "Ocorreu uma falha inesperada e a equipe técnica foi notificada. Tente novamente em alguns minutos.",
  CODIGO_SUPORTE: "Código de suporte: {codigo}",
  COM_CODIGO_SUPORTE: "Se precisar acionar o suporte, informe o código {codigo}.",
};

const t = (chave, params) => {
  try {
    if (!_i18n) _i18n = require("@/i18n/i18n").default;
    const texto = _i18n.t(`ERROS.${chave}`, params);
    if (texto && texto !== `ERROS.${chave}`) return texto;
  } catch (e) {
    // i18n indisponível: cai no fallback abaixo
  }
  // Complemento sem i18n não pode virar outra frase inteira (o usuário leria a
  // mensagem sistêmica duas vezes); título sem i18n fica sem título.
  if (String(chave).startsWith("TITULO.")) return null;
  const texto = FALLBACK[chave] || FALLBACK.SISTEMICO;
  return texto.replace(/\{(\w+)\}/g, (m, nome) =>
    params && params[nome] != null ? params[nome] : m
  );
};

export const textoPadrao = (tipo) => t(CHAVE_I18N[tipo] || "SISTEMICO");
const tituloPadrao = (tipo) => t(`TITULO.${CHAVE_I18N[tipo] || "SISTEMICO"}`);
export const textoCodigoSuporte = (codigo) => t("CODIGO_SUPORTE", { codigo });

const ehHtml = (valor) =>
  typeof valor === "string" && /^\s*<(!doctype|html|head|body)/i.test(valor);

const extrairCodigoSuporte = (err, dados) => {
  const doCorpo =
    (dados &&
      typeof dados === "object" &&
      (dados.codigoSuporte || dados.CodigoSuporte)) ||
    null;
  const headers = (err.response && err.response.headers) || {};
  const doHeader =
    headers["x-codigo-suporte"] || headers["X-Codigo-Suporte"] || null;
  return doCorpo || doHeader || null;
};

// Deixa o nome do campo como o front usa: o FluentValidation manda o
// PropertyName em camelCase, mas o ProblemDetails do ASP.NET manda "Cnpj".
const camelizar = (campo) =>
  String(campo)
    .split(".")
    .map((parte) => (parte ? parte.charAt(0).toLowerCase() + parte.slice(1) : parte))
    .join(".");

// Mensagens do corpo em qualquer um dos formatos que o backend já devolveu:
// errors[] (contrato antigo e atual), erros[].mensagem (contrato novo),
// errors { Campo: [..] } (ProblemDetails do ASP.NET) e mensagem/message soltas.
const mensagensDoCorpo = (dados) => {
  if (typeof dados === "string") return [dados];
  if (!dados || typeof dados !== "object") return [];

  const errors = dados.errors;
  if (Array.isArray(errors) && errors.length) return errors;

  if (Array.isArray(dados.erros) && dados.erros.length) {
    return dados.erros.map((e) => e && e.mensagem);
  }

  if (errors && typeof errors === "object" && !Array.isArray(errors)) {
    return Object.keys(errors).reduce(
      (lista, chave) => lista.concat(errors[chave]),
      []
    );
  }

  if (typeof dados.mensagem === "string") return [dados.mensagem];
  if (typeof dados.message === "string") return [dados.message];
  return [];
};

// Mensagens vindas do backend passam por aqui antes de chegar à tela.
// Devolve { limpas, suprimiuTudo }: se o corpo só trazia conteúdo técnico ou a
// sentinela genérica, não há campo para o usuário corrigir -- quem chama
// reclassifica como sistêmico em vez de dizer "corrija os campos".
const higienizar = (mensagens) => {
  const originais = (mensagens || []).filter(
    (m) => typeof m === "string" && m.trim()
  );
  const limpas = originais
    .filter((m) => !ehSentinelaGenerica(m))
    .map((m) => {
      if (!pareceTecnica(m)) return m.trim();
      // Chegou detalhe interno do servidor. Não exibe: registra e troca pelo
      // texto sistêmico. É a rede que mantém stack trace e SQL fora da tela
      // mesmo se algum serviço regredir.
      // eslint-disable-next-line no-console
      console.error("[erro] conteúdo técnico suprimido da tela:", m);
      return null;
    })
    .filter(Boolean);

  return {
    limpas: limpas.filter((m, i) => limpas.indexOf(m) === i),
    suprimiuTudo: originais.length > 0 && limpas.length === 0,
  };
};

// O tipo declarado pelo backend, quando ele é mais informativo que o status.
const tipoDeclarado = (dados, status) => {
  if (!dados || !Array.isArray(dados.erros) || !dados.erros.length) return null;
  if (!STATUS_GENERICO.includes(status)) return null;

  const declarado = dados.erros.map((e) => e && e.tipo).find(Boolean);
  return declarado ? TIPO_DO_BACKEND[declarado] || null : null;
};

const camposDoCorpo = (dados) => {
  if (!dados || typeof dados !== "object") return [];

  if (Array.isArray(dados.erros)) {
    return dados.erros
      .filter((e) => e && e.campo && e.mensagem && !pareceTecnica(e.mensagem))
      .map((e) => ({ campo: camelizar(e.campo), mensagem: String(e.mensagem) }));
  }

  // ProblemDetails: { errors: { Cnpj: ["..."] } }
  const errors = dados.errors;
  if (errors && typeof errors === "object" && !Array.isArray(errors)) {
    return Object.keys(errors)
      .filter((chave) => chave && chave !== "$")
      .map((chave) => ({
        campo: camelizar(chave),
        mensagem: [].concat(errors[chave]).filter(Boolean).join(" "),
      }))
      .filter((c) => c.mensagem && !pareceTecnica(c.mensagem));
  }

  return [];
};

/**
 * Normaliza qualquer falha do Axios num objeto único que as telas sabem
 * apresentar. Nunca lança: erro dentro do tratador de erro deixa o usuário sem
 * nenhum retorno.
 *
 * @returns {{tipo,titulo,mensagens:string[],campos:Array,codigoSuporte,status,bruto}}
 */
export function interpretarErro(err) {
  try {
    if (axios.isCancel && axios.isCancel(err)) {
      return montar(TipoErro.CANCELADO, [], null, null, err);
    }

    // Sem response: rede fora, DNS, CORS, timeout do cliente -- ou um erro de
    // JavaScript da própria tela, que também não tem o que mostrar ao usuário.
    if (!err || !err.response) {
      if (err && !err.isAxiosError && !err.request) {
        // eslint-disable-next-line no-console
        console.error("[erro] falha que não veio de chamada HTTP:", err);
        return montar(TipoErro.SISTEMICO, [], null, null, err);
      }
      const tipo =
        err && (err.code === "ECONNABORTED" || err.code === "ETIMEDOUT")
          ? TipoErro.INDISPONIVEL
          : TipoErro.REDE;
      return montar(tipo, [], null, null, err);
    }

    const status = err.response.status;
    const dados = err.response.data;
    const tipo = tipoPorStatus(status);
    const codigoSuporte = extrairCodigoSuporte(err, dados);

    // Corpo HTML (WAF, página de erro do gateway): descarta e usa o status.
    // Corpo em texto puro de 5xx ("Internal Server Error", página do proxy) ou
    // longo demais para ser uma frase escrita para o usuário também é corpo cru.
    const textoCru =
      typeof dados === "string" &&
      dados.trim() &&
      (status >= 500 || dados.length > 300);

    if (ehHtml(dados) || textoCru) {
      // eslint-disable-next-line no-console
      console.error("[erro] corpo cru suprimido da tela:", status, dados);
      return montar(tipo, [], codigoSuporte, status, err);
    }

    const { limpas, suprimiuTudo } = higienizar(mensagensDoCorpo(dados));

    // 400 cujo corpo tivemos de descartar (stack trace, erro do Npgsql, ou a
    // sentinela "Ocorreu um erro") não é falha de preenchimento: não há campo
    // para o usuário corrigir. Vira sistêmico, que é o que de fato aconteceu.
    const tipoFinal =
      suprimiuTudo && !ehErroSistemico(tipo)
        ? TipoErro.SISTEMICO
        : tipoDeclarado(dados, status) || tipo;

    return montar(
      tipoFinal,
      // Mensagem de 5xx sem código de suporte embutido ainda serve: o gateway
      // manda texto em português no mesmo envelope. Técnica já foi barrada.
      limpas,
      codigoSuporte,
      status,
      err,
      camposDoCorpo(dados)
    );
  } catch (falhaInterna) {
    // eslint-disable-next-line no-console
    console.error("[erro] falha ao interpretar o erro:", falhaInterna);
    return montar(TipoErro.SISTEMICO, [], null, null, err);
  }
}

function montar(tipo, mensagens, codigoSuporte, status, bruto, campos = []) {
  return {
    tipo,
    titulo: tipo === TipoErro.CANCELADO ? null : tituloPadrao(tipo),
    mensagens:
      tipo === TipoErro.CANCELADO
        ? []
        : mensagens && mensagens.length
        ? mensagens
        : [textoPadrao(tipo)],
    campos,
    // Código de suporte só onde não há o que o usuário faça sozinho: mostrá-lo
    // em erro de validação ensina a abrir chamado para o que se resolve na tela.
    codigoSuporte: ehErroSistemico(tipo) ? codigoSuporte : null,
    status,
    bruto,
  };
}

/**
 * O gateway e o backend já escrevem o código dentro da própria mensagem
 * ("...informe o código de suporte X.") além de mandá-lo em codigoSuporte -- o
 * texto precisa ficar completo para quem só lê errors[]. Quando a mensagem já
 * cita o código, repetir o complemento fazia o usuário ler o código duas vezes.
 */
export const mensagensCitamCodigo = (mensagens, codigoSuporte) =>
  !!codigoSuporte &&
  (mensagens || []).some(
    (m) => typeof m === "string" && m.includes(codigoSuporte)
  );

/**
 * Texto pronto para exibir, já com o código de suporte quando ele é útil.
 */
export function textoDoErro(erroInterpretado) {
  const { mensagens, codigoSuporte } = erroInterpretado;
  const corpo = mensagens.join(" ");
  return codigoSuporte && !mensagensCitamCodigo(mensagens, codigoSuporte)
    ? `${corpo} ${t("COM_CODIGO_SUPORTE", { codigo: codigoSuporte })}`
    : corpo;
}
